import { useEffect, useRef } from 'react';
import { Platform } from 'react-native';

/**
 * Fires `onView` once when the element carrying `nativeID={sectionId}` scrolls
 * at least 50% into view. Web-only (IntersectionObserver); no-ops on native.
 */
export function useSectionView(sectionId: string, onView: () => void): void {
  const onViewRef = useRef(onView);
  onViewRef.current = onView;

  useEffect(() => {
    if (Platform.OS !== 'web' || typeof document === 'undefined') {
      return;
    }
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const element = document.getElementById(sectionId);
    if (!element) {
      return;
    }

    let fired = false;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!fired && entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            fired = true;
            onViewRef.current();
            observer.disconnect();
          }
        }
      },
      { threshold: 0.5 },
    );

    observer.observe(element);
    return () => observer.disconnect();
  }, [sectionId]);
}
